import type {} from './gtm.global';
import { Injectable } from '@angular/core';
import { EventParamsMap } from './events/event-param-map';

/**
 * Google Tag Manager service for type-safe event tracking.
 *
 * Provides type-safe event dispatching to GTM dataLayer. Supports custom
 * event definitions through generic type parameter.
 * 
 * @example
 * ```typescript
 * // Define custom events
 * interface MyEvents extends EventParamsMap {
 *   user_action: {
 *     action_type: string;
 *     item_id: number;
 *   };
 * }
 * 
 * // Use with custom events
 * gtmService = inject(GtmService<MyEvents>);
 * gtmService.sendEvent('user_action', { action_type: 'click', item_id: 123 });
 * ```
 */
@Injectable(
  { providedIn: 'root' }
)
export class GtmService<TEventMap extends EventParamsMap = EventParamsMap> {

  constructor() {
    // Ensure dataLayer is defined
    if (!window.dataLayer) {
      window.dataLayer = [];
    }
  }

  public sendEvent<E extends keyof TEventMap>(
    eventName: E,
    eventParams: E extends keyof EventParamsMap ? EventParamsMap[E] : TEventMap[E]
  ): void {
    window.dataLayer?.push({
      event: eventName,
      ...eventParams,
    });
  }
}
